import axios from "axios";
import { derived, get, writable } from "svelte/store";
import { locale, locale as localeStore } from "./locale";
import { mattressData } from "../data/mattressData";

const { subscribe, update } = writable({});

function getPrice(prices, locale, mattress, mattressType, isPromo: boolean) {
    const key = `product#matras_${mattress}:${mattressType}`;

    const priceData = prices[locale]?.success?.data[key];

    if (!priceData) return "";

    if (isPromo && !hasPromoPrice(priceData)) {
        return '';
    }

    const priceType = isPromo ? 'periodic' : 'regular';

    return new Intl.NumberFormat(locale, { minimumFractionDigits: 2 })
        .format(prices[locale].success.data[key][priceType]["[total]"])
        .replace(/[,\.]00$/, ',-');
}

function hasPromoPrice(prices) {
    return (prices.periodic["[total]"] !== prices.regular["[total]"]);
}

function init(locale: string): void {
    if (get(prices)[locale]) return;

    axios({
        baseURL: `${import.meta.env.VITE_PRICE_MANAGER_ENDPOINT}`,
        url: `/rulesets/auping_prijzen_matraskeuze`,
        method: "POST",
        data: {
            data: mattressData,
            country: locale.toUpperCase().substring(3, 5),
        },
    })
        .then((response) => {
            update((prices) => {
                return Object.assign(prices, { [locale]: mapRulesEngineToPriceManagerData(response.data) });
            });
        })
        .catch((e) => {
            console.error(e);
        });
}

function mapRulesEngineToPriceManagerData(response) {
    const data = response.reduce((result, product, index) => {
        const productKey = getProductId(product);

        return Object.assign(result, {
            [productKey]: product.reduce((result, record) => {
                if (record.type === 'total') {
                    result.regular['[total]'] = record.value

                    if (!('[total]' in result.periodic)) {
                        result.periodic['[total]'] = record.value
                    }
                }

                if (record.type === "promotion") {
                    result.periodic['[total]'] = record.value
                }

                if (record.type === "group") {
                    result.regular[record.label] = record.sub_total.value;
                    result.periodic[record.label] = record.sub_total.value;

                    if ('promotion' in record) {
                        result.periodic[record.label] = record.promotion.value;
                    }
                }

                return result;
            }, { regular: {}, periodic: {} })
        });
    }, {});

    return {
        success: {
            data
        }
    }
}

function getProductId(product): string {
    const group = product.find(record => record.type === 'group');
    const productKey = group.data[0].Omschrijving.split(" ")[0].toLowerCase();

    return `product#matras_${productKey}:${getMattressTypeKey(product)}`;
}

function getMattressTypeKey(product): string {
    const group = product.find(record => record.type === 'group');

    if (group.amount === 2) return 'two-one-person-mattresses';

    const dimensions = group.data[0].Waarde.split('x');

    return +dimensions[0] < 140 ? 'one-person-mattress' : 'two-person-mattress';
}

export const prices = {
    subscribe,
    init,
};

/**
 *  Store to listen to for prices.
 *
 *  @param mattress The slug of the mattress.
 *  @param mattressType The type of the mattress.
 *  @param promo Get the promo price. Defaults to false.
 */
export const price = derived(
    [prices, localeStore],
    ([$prices, $locale]) =>
        (mattress, mattressType, promo = false) => {
            return getPrice($prices, $locale, mattress, mattressType, promo);
        }
);

export const pricesExist = derived([prices, locale], ([$prices, $locale]) => {
    if (!$prices[$locale]?.success?.data) return false;

    return Object.keys($prices[$locale]?.success.data).length;
});
